/**
 * The order and colour of messages, as the panel's message page has them: the
 * worst level first, the newest first within a level, and a scope that is
 * either the whole plant or one circuit.
 *
 * Pure functions; `composables/useActiveMessages.ts` holds the live state.
 */
import type { IDatapoint } from '@/interfaces/IDatapoint'
import type { IMessageConfig } from '@/interfaces/gui'

/** The scope of the whole plant, next to the name of a circuit. */
export const ALL_CIRCUITS = '*'

export type MessageLevel = 'fault' | 'alarm' | 'service' | 'warning' | 'status'

const LEVEL_ORDER: ReadonlyArray<MessageLevel> = ['fault', 'alarm', 'service', 'warning', 'status']

/** 0 for the worst level; unknown types rank behind status. */
export function messagePriority(type: string | undefined): number {
  const index = LEVEL_ORDER.indexOf(type?.toLowerCase() as MessageLevel)
  return index === -1 ? LEVEL_ORDER.length : index
}

/** The worst level among the messages, undefined when there are none. */
export function worstLevel(
  messages: ReadonlyArray<{ readonly type: string }>,
): MessageLevel | undefined {
  let worst: MessageLevel | undefined
  for (const { type } of messages) {
    const level = LEVEL_ORDER[messagePriority(type)]
    if (level && (!worst || messagePriority(level) < messagePriority(worst))) worst = level
  }
  return worst
}

/** The dot colour of a level, as the panel colours it. */
export function levelDotClass(level: MessageLevel | undefined): string {
  switch (level) {
    case 'fault':
    case 'alarm':
      return 'bg-red-500'
    case 'warning':
      return 'bg-amber-400'
    case 'service':
      return 'bg-blue-500'
    default:
      return 'bg-green-500'
  }
}

/** The datapoint a message stands on, and whether it stands while that datapoint is off. */
export function messageTrigger(message: Pick<IMessageConfig, 'datapoint'>): {
  datapoint: string
  inverted: boolean
} {
  const inverted = message.datapoint.startsWith('!')
  return { datapoint: inverted ? message.datapoint.slice(1) : message.datapoint, inverted }
}

/** Whether the message stands for the current value of its datapoint. */
export function isStanding(
  message: Pick<IMessageConfig, 'datapoint'>,
  value: IDatapoint | undefined,
): boolean {
  if (!value) return false
  const { inverted } = messageTrigger(message)
  return inverted ? !value.value : Boolean(value.value)
}

/** Whether a message of the given system belongs to the scope. */
export function inScope(system: string | undefined, scope: string): boolean {
  return scope === ALL_CIRCUITS || system === scope
}

/** Worst level first, newest first within a level. */
export function byLevelThenTime<T extends { type: string; since: number }>(
  entries: ReadonlyArray<T>,
): T[] {
  return [...entries].sort(
    (a, b) => messagePriority(a.type) - messagePriority(b.type) || b.since - a.since,
  )
}
